import React, { useState, useRef, useCallback, useEffect } from 'react';

interface Props {
  src: string;
  onLoad: (info: { naturalWidth: number; naturalHeight: number }) => void;
  onError: () => void;
  shouldFlipHorizontally?: boolean;
  viewportState?: StaticViewportState | null;
  onViewportStateChange?: (state: StaticViewportState) => void;
}

interface Size {
  width: number;
  height: number;
}

export interface StaticViewportState {
  zoomRatio: number;
  panX: number;
  panY: number;
}

const MAX_SCALE = 10;

const StaticImageViewer: React.FC<Props> = ({
  src,
  onLoad,
  onError,
  shouldFlipHorizontally = false,
  viewportState = null,
  onViewportStateChange,
}) => {
  const [displayedSrc, setDisplayedSrc] = useState(src);
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [containerSize, setContainerSize] = useState<Size>({ width: 0, height: 0 });
  const [imageSize, setImageSize] = useState<Size>({ width: 0, height: 0 });
  const [pendingImage, setPendingImage] = useState<{
    src: string;
    naturalWidth: number;
    naturalHeight: number;
  } | null>(null);

  const lastPos = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const suppressNextOnLoadRef = useRef(false);
  const lastEmittedViewportRef = useRef<StaticViewportState | null>(null);

  const clampTranslate = useCallback(
    (
      tx: number,
      ty: number,
      scaleValue: number,
      sizeOverride?: Size,
      containerOverride?: Size,
    ) => {
      const targetImageSize = sizeOverride ?? imageSize;
      const targetContainerSize = containerOverride ?? containerSize;
      const scaledW = targetImageSize.width * scaleValue;
      const scaledH = targetImageSize.height * scaleValue;
      const maxOffsetX = Math.max(0, (scaledW - targetContainerSize.width) / 2);
      const maxOffsetY = Math.max(0, (scaledH - targetContainerSize.height) / 2);
      const clampedX = Math.min(Math.max(tx, -maxOffsetX), maxOffsetX);
      const clampedY = Math.min(Math.max(ty, -maxOffsetY), maxOffsetY);
      return { x: clampedX, y: clampedY };
    },
    [containerSize.width, containerSize.height, imageSize.height, imageSize.width],
  );

  const clampScale = useCallback(
    (value: number) => Math.min(Math.max(value, minScale || 0.1), MAX_SCALE),
    [minScale],
  );

  // Track container size for responsive min scale
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) {
        setContainerSize({ width: rect.width, height: rect.height });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Stage source swaps so the old image stays visible until the next one is loaded.
  useEffect(() => {
    if (!src || src === displayedSrc) return;

    let isCancelled = false;
    const image = new Image();
    image.decoding = 'async';

    image.onload = () => {
      if (isCancelled) return;
      setPendingImage({
        src,
        naturalWidth: image.naturalWidth,
        naturalHeight: image.naturalHeight,
      });
    };

    image.onerror = () => {
      if (isCancelled) return;
      onError();
    };

    image.src = src;

    return () => {
      isCancelled = true;
    };
  }, [displayedSrc, onError, src]);

  // Commit the staged image only when we can compute a fit scale for the current viewport.
  useEffect(() => {
    if (!pendingImage) return;
    if (!containerSize.width || !containerSize.height) return;
    if (pendingImage.src !== src) {
      setPendingImage(null);
      return;
    }

    const computedMin = Math.min(
      containerSize.width / pendingImage.naturalWidth,
      containerSize.height / pendingImage.naturalHeight,
    );

    const nextZoomRatio = Math.max(viewportState?.zoomRatio ?? 1, 1);
    const nextScale = Math.min(Math.max(nextZoomRatio * computedMin, computedMin), MAX_SCALE);
    const nextTranslate = viewportState
      ? clampTranslate(
        viewportState.panX,
        viewportState.panY,
        nextScale,
        { width: pendingImage.naturalWidth, height: pendingImage.naturalHeight },
        containerSize,
      )
      : { x: 0, y: 0 };

    setHasUserInteracted(Boolean(viewportState));
    setImageSize({ width: pendingImage.naturalWidth, height: pendingImage.naturalHeight });
    setMinScale(computedMin);
    setScale(nextScale);
    setTranslate(nextTranslate);
    suppressNextOnLoadRef.current = true;
    setDisplayedSrc(pendingImage.src);
    onLoad({
      naturalWidth: pendingImage.naturalWidth,
      naturalHeight: pendingImage.naturalHeight,
    });
    setPendingImage(null);
  }, [clampTranslate, containerSize, onLoad, pendingImage, src, viewportState]);

  // Recompute minimum scale when sizes change
  useEffect(() => {
    if (!imageSize.width || !containerSize.width || !containerSize.height) return;
    // Fit entire image within the container on load/resize (contain). This avoids initial zoom-in.
    const computedMin = Math.min(
      containerSize.width / imageSize.width,
      containerSize.height / imageSize.height,
    );
    setMinScale(computedMin);
    setScale((prevScale) => {
      const nextScale = hasUserInteracted ? Math.max(prevScale, computedMin) : computedMin;
      setTranslate((prevTranslate) => {
        if (!hasUserInteracted || nextScale === computedMin) return { x: 0, y: 0 };
        return clampTranslate(prevTranslate.x, prevTranslate.y, nextScale);
      });
      return nextScale;
    });
  }, [clampTranslate, containerSize.height, containerSize.width, hasUserInteracted, imageSize.height, imageSize.width]);

  useEffect(() => {
    if (!onViewportStateChange || !minScale || !imageSize.width || !imageSize.height) return;

    const nextViewport: StaticViewportState = {
      zoomRatio: Math.max(scale / minScale, 1),
      panX: translate.x,
      panY: translate.y,
    };

    const last = lastEmittedViewportRef.current;
    if (
      last &&
      Math.abs(last.zoomRatio - nextViewport.zoomRatio) < 0.0001 &&
      Math.abs(last.panX - nextViewport.panX) < 0.5 &&
      Math.abs(last.panY - nextViewport.panY) < 0.5
    ) {
      return;
    }

    lastEmittedViewportRef.current = nextViewport;
    onViewportStateChange(nextViewport);
  }, [imageSize.height, imageSize.width, minScale, onViewportStateChange, scale, translate.x, translate.y]);

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      setHasUserInteracted(true);
      const el = containerRef.current;
      const rect = el?.getBoundingClientRect();
      const pointerX = rect ? e.clientX - rect.left : containerSize.width / 2;
      const pointerY = rect ? e.clientY - rect.top : containerSize.height / 2;
      const centerX = containerSize.width / 2;
      const centerY = containerSize.height / 2;

      setScale((prevScale) => {
        const nextScale = clampScale(prevScale - e.deltaY * 0.001);
        setTranslate((prevTranslate) => {
          if (nextScale === minScale) return { x: 0, y: 0 };

          const ratio = prevScale ? nextScale / prevScale : 1;

          // Keep the point under the cursor stable while zooming.
          // Derivation: p = c + t + s * u  =>  t' = p - c - s' * u
          const nextTx =
            pointerX - centerX - ratio * (pointerX - centerX - prevTranslate.x);
          const nextTy =
            pointerY - centerY - ratio * (pointerY - centerY - prevTranslate.y);

          return clampTranslate(nextTx, nextTy, nextScale);
        });
        return nextScale;
      });
    },
    [clampScale, clampTranslate, containerSize.height, containerSize.width, minScale],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (scale <= minScale) return; // nothing to pan at min zoom
      e.preventDefault();
      setHasUserInteracted(true);
      setDragging(true);
      lastPos.current = { x: e.clientX, y: e.clientY };
    },
    [minScale, scale],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging) return;
      const dx = e.clientX - lastPos.current.x;
      const dy = e.clientY - lastPos.current.y;
      lastPos.current = { x: e.clientX, y: e.clientY };
      setTranslate((prev) => clampTranslate(prev.x + dx, prev.y + dy, scale));
    },
    [clampTranslate, dragging, scale],
  );

  const handleMouseUp = useCallback(() => {
    setDragging(false);
  }, []);

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      if (suppressNextOnLoadRef.current) {
        suppressNextOnLoadRef.current = false;
        return;
      }
      if (!pendingImage) {
        onLoad({ naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight });
      }
    },
    [onLoad, pendingImage],
  );

  const cursor = scale > minScale ? 'move' : 'default';

  return (
    <div
      ref={containerRef}
      className="static-image-container"
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{ cursor }}
    >
      <img
        ref={imgRef}
        src={displayedSrc}
        alt="Survey"
        draggable={false}
        onLoad={handleImageLoad}
        onError={onError}
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scaleX(${shouldFlipHorizontally ? -1 : 1}) scale(${scale})`,
          transformOrigin: 'center center',
          cursor,
          userSelect: 'none',
          pointerEvents: 'auto',
          willChange: 'transform',
          maxWidth: 'none',
          maxHeight: 'none',
        }}
      />
    </div>
  );
};

export default StaticImageViewer;
